import React, { useState, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, TrendingUp, DollarSign, Users, FileText, MapPin, BarChart3, RefreshCw } from 'lucide-react';
import { statisticsAPI } from '../services/api';
import { StatisticsDashboard } from '../components/StatisticsCharts';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';
import { useToast } from '../contexts/ToastContext';
import { formatCurrency } from '../utils/formatters';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import 'jspdf-font';

/** 통계 기간 선택 시 년도 드롭다운 최소 연도 */
const MIN_STATISTICS_YEAR = 1950;

const Statistics = () => {
  const { user } = useAuth();
  const { success, error: showError } = useToast();
  const now = new Date();
  const toLocalYmd = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1); // 1-12
  const [selectedDay, setSelectedDay] = useState(now.getDate()); // 1-31
  const [period, setPeriod] = useState('year');
  const [dateRange, setDateRange] = useState(() => ({
    startDate: toLocalYmd(new Date(now.getFullYear(), 0, 1)),
    endDate: toLocalYmd(new Date(now.getFullYear(), 11, 31))
  }));
  const [activeTab, setActiveTab] = useState('overview');
  const pdfRef = useRef(null);
  const pdfPage1Ref = useRef(null);
  const pdfPage2Ref = useRef(null);
  const pdfPage3Ref = useRef(null);
  const pdfPage4Ref = useRef(null);
  const pdfPage5Ref = useRef(null);

  const periodLabelMap = { month: '월', year: '년' };
  const cemeteryName = user?.cemetery?.name || user?.cemeteryName || '';
  const formatDotDate = (yyyyMmDd) => {
    if (!yyyyMmDd) return '';
    const [y, m, d] = yyyyMmDd.split('-').map(v => parseInt(v, 10));
    if (!y || !m || !d) return '';
    return `${y}. ${m}. ${d}.`;
  };

  // 기간 제목(개요 탭 2번째 줄): 년 -> YYYY년, 월 -> YYYY년 MM월, 일 -> YYYY년 MM월 DD일
  const getPeriodTitle = () => {
    if (period === 'year') {
      return `${selectedYear}년`;
    } else if (period === 'month') {
      const mm = String(selectedMonth).padStart(2, '0');
      return `${selectedYear}년 ${mm}월`;
    } else if (period === 'day') {
      const mm = String(selectedMonth).padStart(2, '0');
      const dd = String(selectedDay).padStart(2, '0');
      return `${selectedYear}년 ${mm}월 ${dd}일`;
    }
    return '';
  };

  // 통계 데이터 조회
  const { data: statistics, isLoading, error, refetch } = useQuery({
    queryKey: ['statistics', dateRange, period],
    queryFn: () => statisticsAPI.getAll({
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      period: period
    }),
    select: (response) => {
      console.log('=== React Query 응답 ===');
      console.log('전체 응답:', response);
      console.log('response.data:', response.data);
      // 중첩된 data 구조 처리
      return response.data.data || response.data;
    },
    enabled: !!dateRange.startDate && !!dateRange.endDate,
    staleTime: 5 * 60 * 1000, // 5분간 데이터 신선도 유지
    cacheTime: 10 * 60 * 1000, // 10분간 캐시 유지
    refetchOnWindowFocus: false, // 윈도우 포커스 시 재요청 방지
    refetchOnMount: false // 컴포넌트 마운트 시 재요청 방지
  });

  // 엑셀 다운로드
  const handleExcelDownload = () => {
    if (!statistics) {
      showError('다운로드할 데이터가 없습니다.');
      return;
    }

    try {
      const wb = XLSX.utils.book_new();
      const monthlyContracts = statistics.byMonth?.[statistics.byMonth.length - 1]?.contract_count || 0;
      const todayYmd = toLocalYmd(new Date());
      const thisYear = new Date().getFullYear();
      const thisMonth = new Date().getMonth() + 1; // 1–12
      const isCurrentYearPeriod = period === 'year' && selectedYear === thisYear;
      const isCurrentMonthPeriod =
        period === 'month' && selectedYear === thisYear && selectedMonth === thisMonth;
      const excelStartDate = dateRange.startDate;
      const excelEndDate =
        isCurrentYearPeriod || isCurrentMonthPeriod ? todayYmd : dateRange.endDate;

      // 엑셀: 탭별 시트 분리하지 않고 한 시트에 모두 누적
      const contractTypeRows = statistics.contracts?.by_type || [];
      const tombTypeRows = statistics.byTombType || [];
      const regionRows = statistics.byRegion || [];
      const stoneRows = statistics.byStone || [];
      const revenueItemRows = statistics.byRevenueItem || [];

      const stoneTotals = stoneRows.reduce(
        (acc, item) => {
          acc.count += Number(item.count || 0);
          acc.sales += Number(item.total_sales || 0);
          return acc;
        },
        { count: 0, sales: 0 }
      );

      const excelAoA = [
        ['구분', '값'],
        ['조회 기간', `${excelStartDate} ~ ${excelEndDate}`],
        ['총 매출', formatCurrency(statistics.sales?.total_sales || 0)],
        ['총 계약 건수', `${statistics.contracts?.total_contracts || 0}건`],
        ['평균 매출', formatCurrency(statistics.sales?.average_sales || 0)],
        [period === 'day' ? '조회일 계약' : '이번 달 계약', `${monthlyContracts}건`],
        ['', ''],
        ['[항목 별 매출 통계]', ''],
        ['항목', '금액'],
        ...revenueItemRows.map((item) => [item.item || '-', formatCurrency(item.amount || 0)]),
        ['', ''],

        ['[매출 통계]', '', '', ''],
        ['기간', '매출', '계약 건수', '평균 매출'],
        ...(statistics.byMonth || []).length > 0
          ? (statistics.byMonth || []).map((item) => [
              item.period || '-',
              formatCurrency(item.sales || 0),
              `${item.contract_count || 0}건`,
              formatCurrency((item.sales || 0) / (item.contract_count || 1))
            ])
          : [['-', '-', '-', '-']],
        ['', '', '', ''],

        ['[계약 구분별 통계]', '', ''],
        ['구분', '건수', '비율'],
        ...(contractTypeRows.length > 0
          ? contractTypeRows.map((item) => [
              item.type || '-',
              `${item.count || 0}건`,
              `${item.percentage || 0}%`
            ])
          : [['-', '-', '-']]),
        ['', '', ''],

        ['[묘지 형태별 통계]', '', ''],
        ['묘지 형태', '계약 건수', '총 매출'],
        ...(tombTypeRows.length > 0
          ? tombTypeRows.map((item) => [
              item.tomb_type || '-',
              `${item.contract_count || 0}건`,
              formatCurrency(item.total_sales || 0)
            ])
          : [['-', '-', '-']]),
        ['', '', ''],

        ['[지역별 통계]', '', '', '', ''],
        ['광역지자체', '계약 건수', '총 매출', '평균 매출', '주요 묘지'],
        ...(regionRows.length > 0
          ? regionRows.map((item) => [
              item.region_name || '-',
              `${item.contract_count || 0}건`,
              formatCurrency(item.total_sales || 0),
              formatCurrency((item.total_sales || 0) / (item.contract_count || 1)),
              item.main_cemetery || '-'
            ])
          : [['-', '-', '-', '-', '-']]),
        ['', '', '', '', ''],

        ['[석물 통계]', '', ''],
        ['석물명', '판매 갯수', '매출액'],
        ...(stoneRows.length > 0
          ? stoneRows.map((item) => [
              item.stone_name || '-',
              `${item.count || 0}개`,
              formatCurrency(item.total_sales || 0)
            ])
          : [['-', '-', '-']]),
        ['합계', `${stoneTotals.count}개`, formatCurrency(stoneTotals.sales || 0)]
      ];

      const ws = XLSX.utils.aoa_to_sheet(excelAoA);
      XLSX.utils.book_append_sheet(wb, ws, '통계보고서');

      // 파일 다운로드
      const fileName = `통계보고서_${excelStartDate}_${excelEndDate}.xlsx`;
      XLSX.writeFile(wb, fileName);

      success('통계 보고서가 다운로드되었습니다.');
    } catch (error) {
      console.error('엑셀 다운로드 실패:', error);
      showError('엑셀 다운로드에 실패했습니다.');
    }
  };

  const handlePdfDownload = async () => {
    try {
      if (!statistics) {
        showError('PDF로 내보낼 데이터가 없습니다.');
        return;
      }
      const page1 = pdfPage1Ref.current;
      const page2 = pdfPage2Ref.current;
      const page3 = pdfPage3Ref.current;
      const page4 = pdfPage4Ref.current;
      const page5 = pdfPage5Ref.current;
      if (!page1 || !page2 || !page3 || !page4 || !page5) return;

      // 버튼 숨김 처리 (예방적)
      const hiddenEls = [];
      pdfRef.current.querySelectorAll('.btn, button').forEach(el => {
        hiddenEls.push({ el, display: el.style.display });
        el.style.display = 'none';
      });

      // 차트 렌더링 대기 (비동기 렌더 반영)
      await new Promise(resolve => setTimeout(resolve, 500));

      const canvas1 = await html2canvas(page1, { scale: 2, useCORS: true, windowWidth: 1200 });
      const canvas2 = await html2canvas(page2, { scale: 2, useCORS: true, windowWidth: 1200 });
      const canvas3 = await html2canvas(page3, { scale: 2, useCORS: true, windowWidth: 1200 });
      const canvas4 = await html2canvas(page4, { scale: 2, useCORS: true, windowWidth: 1200 });
      const canvas5 = await html2canvas(page5, { scale: 2, useCORS: true, windowWidth: 1200 });

      // 복구
      hiddenEls.forEach(({ el, display }) => (el.style.display = display));

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const margin = 10; // mm
      const headerHeight = 0; // 헤더 이미지는 사용하지 않음
      const usableWidth = pageWidth - margin * 2;
      const usableHeight = pageHeight - margin * 2 - headerHeight;

      // Helper: contain-fit addImage
      const addContainedImage = (canvas, isFirstPage, pageIndex) => {
        const imgData = canvas.toDataURL('image/png');
        let drawWidth = usableWidth;
        let drawHeight = (canvas.height * drawWidth) / canvas.width;
        if (drawHeight > usableHeight) {
          drawHeight = usableHeight;
          drawWidth = (canvas.width * drawHeight) / canvas.height;
        }
        const x = margin + (usableWidth - drawWidth) / 2;
        const y = margin + headerHeight;
        if (!isFirstPage) pdf.addPage();
        // 본문 이미지
        pdf.addImage(imgData, 'PNG', x, y, drawWidth, drawHeight);
      };

      // Page 1
      addContainedImage(canvas1, true, 1);
      // Page 2
      addContainedImage(canvas2, false, 2);
      // Page 3
      addContainedImage(canvas3, false, 3);
      // Page 4
      addContainedImage(canvas4, false, 4);
      // Page 5
      addContainedImage(canvas5, false, 5);

      // 푸터(다운로드 시점, 페이지 번호) 및 워터마크
      const timestamp = new Date().toLocaleString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }).replace(/\. /g, '/').replace(' ', ' ');
      const pageCount = pdf.getNumberOfPages();
      
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        
        // 워터마크 추가 (묘지명) - 이미지로 생성하여 한글 깨짐 방지
        if (cemeteryName) {
          // 워터마크용 캔버스 생성
          const watermarkCanvas = document.createElement('canvas');
          const ctx = watermarkCanvas.getContext('2d');
          watermarkCanvas.width = 200; // 400 -> 200 (절반)
          watermarkCanvas.height = 50; // 100 -> 50 (절반)
          
          // 투명한 배경
          ctx.clearRect(0, 0, watermarkCanvas.width, watermarkCanvas.height);
          
          // 텍스트 스타일 설정
          ctx.fillStyle = 'rgba(200, 200, 200, 0.1)'; // 회색, 10% 투명도
          ctx.font = '24px Arial, sans-serif'; // 48px -> 24px (절반)
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          
          // 텍스트 그리기
          ctx.fillText(cemeteryName, watermarkCanvas.width / 2, watermarkCanvas.height / 2);
          
          // 이미지를 PDF에 추가
          const watermarkDataUrl = watermarkCanvas.toDataURL('image/png');
          pdf.addImage(watermarkDataUrl, 'PNG', 
              (pageWidth - 100) / 2, (pageHeight - 25) / 2, 
              100, 25); // 크기 절반으로 축소
        }
        
        // 푸터
        pdf.setTextColor(0, 0, 0); // 검은색
        pdf.setFontSize(10);
        pdf.text(timestamp, pageWidth / 2, pageHeight - 6, { align: 'center' });
        pdf.text(String(i), pageWidth - margin, pageHeight - 6, { align: 'right' });
      }

      const title = `통계보고서_${dateRange.startDate}_${dateRange.endDate}.pdf`;
      pdf.save(title);
    } catch (e) {
      console.error('PDF 생성 실패:', e);
      showError('PDF 생성 중 오류가 발생했습니다.');
    }
  };

  // 디버깅: 데이터 상태 확인
  console.log('=== Statistics 컴포넌트 상태 ===');
  console.log('isLoading:', isLoading);
  console.log('error:', error);
  console.log('statistics:', statistics);
  console.log('statistics?.sales:', statistics?.sales);
  console.log('statistics?.contracts:', statistics?.contracts);

  if (!hasPermission(user?.role, 'STATISTICS_VIEW')) {
    return (
      <div className="flex-center min-h-screen">
        <div className="text-center">
          <TrendingUp size={64} className="mx-auto mb-4 text-gray-300" />
          <h2 className="text-xl font-semibold text-gray-600 mb-2">접근 권한이 없습니다</h2>
          <p className="text-gray-500">통계 조회 권한이 필요합니다.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
        통계 데이터를 불러오는 중...
      </div>
    );
  }

  if (error) {
    return (
      <div className="error">
        통계 데이터를 불러오는 중 오류가 발생했습니다: {error.message}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">통계 관리</h1>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => refetch()}
            disabled={isLoading}
            className="btn btn-outline"
          >
            <RefreshCw size={16} className={`mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            {isLoading ? '새로고침 중...' : '새로고침'}
          </button>
          <button
            onClick={handleExcelDownload}
            className="btn btn-outline"
            disabled={!statistics}
          >
            <Download size={16} className="mr-2" />
            엑셀 다운로드
          </button>
          <button
            onClick={handlePdfDownload}
            className="btn btn-outline"
            disabled={!statistics}
          >
            <Download size={16} className="mr-2" />
            PDF 다운로드
          </button>
        </div>
      </div>

      {/* 날짜 범위 선택 */}
      <div className="card">
        <div className="card-body">
          <div className={`grid grid-cols-1 gap-4 items-end ${period === 'day' ? 'md:grid-cols-5' : period === 'month' ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
            <div>
              <label className="form-label">기간 단위</label>
              <select
                value={period}
                onChange={(e) => {
                  const p = e.target.value;
                  setPeriod(p);
                }}
                className="select"
              >
                <option value="year">년</option>
                <option value="month">월</option>
                <option value="day">일</option>
              </select>
            </div>
            {/* 년도 선택 */}
            <div>
              <label className="form-label">년도</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
                className="select"
              >
                {Array.from({ length: now.getFullYear() - MIN_STATISTICS_YEAR + 1 }).map((_, idx) => {
                  const y = now.getFullYear() - idx;
                  return (
                    <option key={y} value={y}>{y}년</option>
                  );
                })}
              </select>
            </div>
            {/* 월 선택 (월 단위 또는 일 단위일 때) */}
            {(period === 'month' || period === 'day') && (
              <div>
                <label className="form-label">월</label>
                <select
                  value={selectedMonth}
                  onChange={(e) => {
                    const newMonth = parseInt(e.target.value, 10);
                    setSelectedMonth(newMonth);
                    // 월이 변경되면 일자도 조정 (해당 월의 마지막 날짜를 초과하지 않도록)
                    const daysInMonth = new Date(selectedYear, newMonth, 0).getDate();
                    if (selectedDay > daysInMonth) {
                      setSelectedDay(daysInMonth);
                    }
                  }}
                  className="select"
                >
                  {Array.from({ length: 12 }).map((_, idx) => {
                    const m = idx + 1;
                    return (
                      <option key={m} value={m}>{m}월</option>
                    );
                  })}
                </select>
              </div>
            )}
            {/* 일 선택 (일 단위일 때만) */}
            {period === 'day' && (
              <div>
                <label className="form-label">일</label>
                <select
                  value={selectedDay}
                  onChange={(e) => setSelectedDay(parseInt(e.target.value, 10))}
                  className="select"
                >
                  {Array.from({ length: new Date(selectedYear, selectedMonth, 0).getDate() }).map((_, idx) => {
                    const d = idx + 1;
                    return (
                      <option key={d} value={d}>{d}일</option>
                    );
                  })}
                </select>
              </div>
            )}
            <div>
              <button
                onClick={() => {
                  if (period === 'year') {
                    const start = new Date(selectedYear, 0, 1);
                    const end = new Date(selectedYear, 11, 31);
                    setDateRange({
                      startDate: toLocalYmd(start),
                      endDate: toLocalYmd(end)
                    });
                  } else if (period === 'month') {
                    const start = new Date(selectedYear, selectedMonth - 1, 1);
                    const end = new Date(selectedYear, selectedMonth, 0);
                    setDateRange({
                      startDate: toLocalYmd(start),
                      endDate: toLocalYmd(end)
                    });
                  } else if (period === 'day') {
                    const selectedDate = new Date(selectedYear, selectedMonth - 1, selectedDay);
                    const dateStr = toLocalYmd(selectedDate);
                    setDateRange({
                      startDate: dateStr,
                      endDate: dateStr
                    });
                  }
                  // refetch는 dateRange state 변경 후 queryKey로 자동 반영됨
                  setTimeout(() => refetch(), 0);
                }}
                className="btn btn-primary w-full"
              >
                <BarChart3 size={16} className="mr-2" />
                조회
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 탭 네비게이션 */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          {[
            { id: 'overview', name: '개요', icon: TrendingUp },
            { id: 'sales', name: '매출 통계', icon: DollarSign },
            { id: 'contracts', name: '계약 통계', icon: FileText },
            { id: 'region', name: '지역별 통계', icon: MapPin },
            { id: 'stone', name: '석물 통계', icon: BarChart3 },
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                {/* <Icon size={16} /> */}
                <span>{tab.name}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* 탭 콘텐츠 */}
      <div className="space-y-6">
        {/* 개요 탭 */}
        {activeTab === 'overview' && statistics && (
          <>
            {/* 보고서 제목 */}
            <div className="flex items-center justify-center">
              <h1 className="text-xl font-bold text-gray-900 text-center">
                {cemeteryName ? `${cemeteryName} - 통계 보고서` : '통계 보고서'}
              </h1>
            </div>
            {/* 기간 제목 */}
            <div className="flex items-center justify-center">
              <h3 className="text-lg font-semibold text-gray-900 text-center">
                {getPeriodTitle()}
              </h3>
            </div>

            {/* 요약 카드 */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="card">
                <div className="card-body">
                  <div className="flex items-center">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <DollarSign size={24} className="text-blue-600" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">총 매출</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {formatCurrency(statistics.sales?.total_sales || 0)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-body">
                  <div className="flex items-center">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <FileText size={24} className="text-green-600" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">총 계약 건수</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {statistics.contracts?.total_contracts || 0}건
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-body">
                  <div className="flex items-center">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Users size={24} className="text-purple-600" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">평균 매출</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {formatCurrency(statistics.sales?.average_sales || 0)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 통계 차트 */}
            <StatisticsDashboard
              salesData={statistics.byMonth || []}
              contractData={statistics.byMonth || []}
              tombTypeData={statistics.byTombType || []}
              regionalData={statistics.byRegion || []}
              costData={statistics.byCostType || []}
              revenueItemData={statistics.byRevenueItem || []}
              contractTypeData={statistics.byContractType || null}
              summaryData={{
                total_sales: statistics.sales?.total_sales || 0,
                total_contracts: statistics.contracts?.total_contracts || 0,
                average_sales: statistics.sales?.average_sales || 0,
                monthly_contracts: statistics.byMonth?.[statistics.byMonth.length - 1]?.contract_count || 0
              }}
              period={period}
            />
          </>
        )}

        {/* 매출 통계 탭 */}
        {activeTab === 'sales' && statistics && (
          <div className="space-y-6">
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-semibold">매출 추이</h3>
              </div>
              <div className="card-body">
                <div className="overflow-x-auto">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>기간</th>
                        <th>매출</th>
                        <th>계약 건수</th>
                        <th>평균 매출</th>
                      </tr>
                    </thead>
                    <tbody>
                      {statistics.byMonth?.map((item, index) => (
                        <tr key={index}>
                          <td className="font-medium">{item.period}</td>
                          <td className="font-medium text-left">
                            {formatCurrency(item.sales || 0)}
                          </td>
                          <td className="text-left">{item.contract_count}건</td>
                          <td className="text-left">
                            {formatCurrency((item.sales || 0) / (item.contract_count || 1))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 계약 통계 탭 */}
        {activeTab === 'contracts' && statistics && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* 계약 구분별 통계 */}
              <div className="card">
                <div className="card-header">
                  <h3 className="text-lg font-semibold">계약 구분별 통계</h3>
                </div>
                <div className="card-body">
                  <div className="overflow-x-auto">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>구분</th>
                          <th>건수</th>
                          <th>비율</th>
                        </tr>
                      </thead>
                      <tbody>
                        {statistics.contracts?.by_type?.map((item, index) => (
                          <tr key={index}>
                            <td>{item.type}</td>
                            <td>{item.count}건</td>
                            <td>{item.percentage}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* 묘지 형태별 통계 */}
              <div className="card">
                <div className="card-header">
                  <h3 className="text-lg font-semibold">묘지 형태별 통계</h3>
                </div>
                <div className="card-body">
                  <div className="overflow-x-auto">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>묘지 형태</th>
                          <th>계약 건수</th>
                          <th>총 매출</th>
                        </tr>
                      </thead>
                      <tbody>
                        {statistics.byTombType?.map((item, index) => (
                          <tr key={index}>
                            <td>{item.tomb_type}</td>
                            <td>{item.contract_count}건</td>
                            <td>{formatCurrency(item.total_sales)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 지역별 통계 탭 */}
        {activeTab === 'region' && statistics && (
          <div className="space-y-6">
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-semibold">지역별 통계 (연고자 주소 기준)</h3>
              </div>
              <div className="card-body">
                <div className="overflow-x-auto">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>광역지자체</th>
                        <th>계약 건수</th>
                        <th>총 매출</th>
                        <th>평균 매출</th>
                        <th>주요 묘지</th>
                      </tr>
                    </thead>
                    <tbody>
                      {statistics.byRegion?.map((item, index) => (
                        <tr key={index}>
                          <td className="font-medium">{item.region_name}</td>
                          <td>{item.contract_count}건</td>
                          <td className="font-medium text-left">
                            {formatCurrency(item.total_sales)}
                          </td>
                          <td className="text-left">
                            {formatCurrency((item.total_sales || 0) / (item.contract_count || 1))}
                          </td>
                          <td>{item.main_cemetery || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 석물 통계 탭 */}
        {activeTab === 'stone' && statistics && (
          <div className="space-y-6">
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-semibold">석물 통계</h3>
              </div>
              <div className="card-body">
                <div className="overflow-x-auto">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>석물명</th>
                        <th>판매 갯수</th>
                        <th>매출액</th>
                      </tr>
                    </thead>
                    <tbody>
                      {statistics.byStone && statistics.byStone.length > 0 ? (
                        <>
                          {statistics.byStone.map((item, index) => (
                            <tr key={index}>
                              <td className="font-medium">{item.stone_name}</td>
                              <td>{item.count}개</td>
                              <td className="text-left">{formatCurrency(item.total_sales || 0)}</td>
                            </tr>
                          ))}
                          {(() => {
                            const totals = (statistics.byStone || []).reduce(
                              (acc, item) => {
                                acc.count += Number(item.count || 0);
                                acc.sales += Number(item.total_sales || 0);
                                return acc;
                              },
                              { count: 0, sales: 0 }
                            );
                            return (
                              <tr className="font-bold">
                                <td>합계</td>
                                <td>{totals.count}개</td>
                                <td className="text-left">{formatCurrency(totals.sales || 0)}</td>
                              </tr>
                            );
                          })()}
                        </>
                      ) : (
                        <tr>
                          <td colSpan="3" className="text-center text-gray-500">
                            데이터가 없습니다.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 숨김: PDF용 네 페이지 렌더링 */}
      {statistics && (
        <div ref={pdfRef} style={{ position: 'absolute', left: '-10000px', top: 0, width: '1200px' }}>
          {/* Page 1: 개요 + 차트 */}
          <div ref={pdfPage1Ref} style={{ background: '#fff', padding: '20px' }}>
            {/* 보고서 제목 */}
            <h1 style={{ fontSize: '20px', fontWeight: 700, margin: 0, textAlign: 'center' }}>
              {cemeteryName ? `${cemeteryName} - 통계 보고서` : '통계 보고서'}
            </h1>
            {/* 기간/단위 제목 */}
            <h2 style={{ fontSize: '16px', fontWeight: 600, margin: '8px 0 12px', textAlign: 'center' }}>
              {getPeriodTitle()}
            </h2>

            {/* 개요 */}
            <div style={{ marginBottom: '16px' }}>
              <h3 style={{ fontWeight: 600, marginBottom: '8px' }}>개요</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '12px' }}>
                <div className="card"><div className="card-body"><div className="flex items-center"><div className="ml-0"><p className="text-sm font-medium text-gray-600">총 매출</p><p className="text-xl font-bold text-gray-900">{formatCurrency(statistics.sales?.total_sales || 0)}</p></div></div></div></div>
                <div className="card"><div className="card-body"><div className="flex items-center"><div className="ml-0"><p className="text-sm font-medium text-gray-600">총 계약 건수</p><p className="text-xl font-bold text-gray-900">{statistics.contracts?.total_contracts || 0}건</p></div></div></div></div>
                <div className="card"><div className="card-body"><div className="flex items-center"><div className="ml-0"><p className="text-sm font-medium text-gray-600">평균 매출</p><p className="text-xl font-bold text-gray-900">{formatCurrency(statistics.sales?.average_sales || 0)}</p></div></div></div></div>
              </div>
            </div>

            {/* 차트 */}
            <div style={{ marginBottom: '0px' }}>
              <h3 style={{ fontWeight: 600, marginBottom: '8px' }}>차트</h3>
              <StatisticsDashboard
                salesData={statistics.byMonth || []}
                contractData={statistics.byMonth || []}
                tombTypeData={statistics.byTombType || []}
                regionalData={statistics.byRegion || []}
                costData={statistics.byCostType || []}
                revenueItemData={statistics.byRevenueItem || []}
                contractTypeData={statistics.byContractType || null}
                summaryData={{
                  total_sales: statistics.sales?.total_sales || 0,
                  total_contracts: statistics.contracts?.total_contracts || 0,
                  average_sales: statistics.sales?.average_sales || 0,
                  monthly_contracts: statistics.byMonth?.[statistics.byMonth.length - 1]?.contract_count || 0
                }}
                period={period}
              />
            </div>
          </div>

          {/* Page 2: 매출 통계 */}
          <div ref={pdfPage2Ref} style={{ background: '#fff', padding: '20px' }}>
            <h3 style={{ fontWeight: 600, marginBottom: '8px' }}>매출 통계</h3>
            <table className="table">
              <thead>
                <tr>
                  <th>기간</th>
                  <th>매출</th>
                  <th>계약 건수</th>
                  <th>평균 매출</th>
                </tr>
              </thead>
              <tbody>
                {statistics.byMonth?.map((item, index) => (
                  <tr key={index}>
                    <td className="font-medium">{item.period}</td>
                    <td className="font-medium text-left">{formatCurrency(item.sales || 0)}</td>
                    <td className="text-center">{item.contract_count}건</td>
                    <td className="text-left">{formatCurrency((item.sales || 0) / (item.contract_count || 1))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Page 3: 계약 통계 */}
          <div ref={pdfPage3Ref} style={{ background: '#fff', padding: '20px' }}>
            <h3 style={{ fontWeight: 600, marginBottom: '8px' }}>계약 통계</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '12px' }}>
              <div>
                <h4 style={{ fontWeight: 600, marginBottom: '6px' }}>계약 구분별 통계</h4>
                <table className="table">
                  <thead>
                    <tr>
                      <th>구분</th>
                      <th>건수</th>
                      <th>비율</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statistics.contracts?.by_type?.map((item, index) => (
                      <tr key={index}>
                        <td>{item.type}</td>
                        <td>{item.count}건</td>
                        <td>{item.percentage}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div>
                <h4 style={{ fontWeight: 600, marginBottom: '6px' }}>묘지 형태별 통계</h4>
                <table className="table">
                  <thead>
                    <tr>
                      <th>묘지 형태</th>
                      <th>계약 건수</th>
                      <th>총 매출</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statistics.byTombType?.map((item, index) => (
                      <tr key={index}>
                        <td>{item.tomb_type}</td>
                        <td>{item.contract_count}건</td>
                        <td>{formatCurrency(item.total_sales)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Page 4: 지역별 통계 */}
          <div ref={pdfPage4Ref} style={{ background: '#fff', padding: '20px' }}>
            <h3 style={{ fontWeight: 600, marginBottom: '8px' }}>지역별 통계 (연고자 주소 기준)</h3>
            <table className="table">
              <thead>
                <tr>
                  <th>광역지자체</th>
                  <th>계약 건수</th>
                  <th>총 매출</th>
                  <th>평균 매출</th>
                  <th>주요 묘지</th>
                </tr>
              </thead>
              <tbody>
                {statistics.byRegion?.map((item, index) => (
                  <tr key={index}>
                    <td className="font-medium">{item.region_name}</td>
                    <td>{item.contract_count}건</td>
                    <td className="font-medium text-left">{formatCurrency(item.total_sales)}</td>
                    <td className="text-left">{formatCurrency((item.total_sales || 0) / (item.contract_count || 1))}</td>
                    <td>{item.main_cemetery || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Page 5: 석물 통계 */}
          <div ref={pdfPage5Ref} style={{ background: '#fff', padding: '20px' }}>
            <h3 style={{ fontWeight: 600, marginBottom: '8px' }}>석물 통계</h3>
            <table className="table">
              <thead>
                <tr>
                  <th>석물명</th>
                  <th>판매 갯수</th>
                  <th>매출액</th>
                </tr>
              </thead>
              <tbody>
                {statistics.byStone?.length > 0 ? (
                  <>
                    {statistics.byStone.map((item, index) => (
                      <tr key={index}>
                        <td className="font-medium">{item.stone_name}</td>
                        <td>{item.count}개</td>
                        <td className="text-left">{formatCurrency(item.total_sales || 0)}</td>
                      </tr>
                    ))}
                    {(() => {
                      const totals = (statistics.byStone || []).reduce(
                        (acc, item) => {
                          acc.count += Number(item.count || 0);
                          acc.sales += Number(item.total_sales || 0);
                          return acc;
                        },
                        { count: 0, sales: 0 }
                      );
                      return (
                        <tr className="font-bold">
                          <td>합계</td>
                          <td>{totals.count}개</td>
                          <td className="text-left">{formatCurrency(totals.sales || 0)}</td>
                        </tr>
                      );
                    })()}
                  </>
                ) : (
                  <tr>
                    <td colSpan="3" className="text-center text-gray-500">
                      데이터가 없습니다.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Statistics; 